import PictureImage from '../Common/PictureImage';
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import Footer from "../Layouts/Footer";
import "./layout.css";

// ─── Types ───────────────────────────────────────────────────────────────────
interface Step {
  id: number;
  icon: string;
  title: string;
  desc: ReactNode;
}

interface Feature {
  icon: string;
  title: string;
  desc: ReactNode;
}

interface Stat {
  value: number;
  suffix: string;
  label: string;
}

const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";
const PAGE_BG = "#ffffff";
const PAGE_ALT = "#f8fafc";
const PAGE_BORDER = "#e2e8f0";
const TEXT_DARK = "#0f172a";
const TEXT_MID = "#334155";
const TEXT_SOFT = "#64748b";
const ACCENT = "#f59338";
const ACCENT_RGB = "245, 147, 56";

const inlineLinkOnDark: React.CSSProperties = {
  color: "#fbbf24",
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

const inlineLinkOnLight: React.CSSProperties = {
  color: ACCENT,
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

// ─── Data ────────────────────────────────────────────────────────────────────
const STEPS: Step[] = [
  { id: 1, icon: "📦", title: "Receive Goods", desc: "Inventory received directly from suppliers worldwide and booked into stock at our UK prep centre." },
  { id: 2, icon: "🔍", title: "Quality Check", desc: "Every unit inspected for quality, defects, and product accuracy before prep work begins." },
  {
    id: 3,
    icon: "🏷️",
    title: "Label & Brand",
    desc: (
      <>
        <Link to="/blog/fnsku-labelling-service-uk" style={inlineLinkOnLight}>FNSKU labels</Link> and custom branding applied accurately to every unit.
      </>
    ),
  },
  {
    id: 4,
    icon: "🎁",
    title: "Bundle & Pack",
    desc: (
      <>
        Professional kitting, bundling, and protective packaging — including compliant{" "}
        <Link to="/blog/amazon-poly-bag-requirements-uk" style={inlineLinkOnLight}>poly bagging</Link> — completed to specification.
      </>
    ),
  },
  {
    id: 5,
    icon: "🚀",
    title: "Ship to Amazon FBA",
    desc: (
      <>
        Amazon-compliant <Link to="/amazon-fba-shipments" style={inlineLinkOnLight}>FBA shipments</Link> dispatched on time, ensuring smooth fulfilment operations.
      </>
    ),
  },
];

const FEATURES: Feature[] = [
  {
    icon: "🏷️",
    title: "FNSKU Labelling",
    desc: (
      <>
        Barcode-verified <Link to="/blog/fnsku-labelling-service-uk" style={inlineLinkOnLight}>FNSKU labelling</Link> applied precisely to Amazon requirements.
      </>
    ),
  },
  {
    icon: "✨",
    title: "Custom Branding",
    desc: (
      <>
        Branded inserts, stickers, and packaging applied to private label products — see our{" "}
        <Link to="/blog/fba-prep-private-label-sellers-uk" style={inlineLinkOnLight}>private label FBA prep UK guide</Link>.
      </>
    ),
  },
  {
    icon: "🛍️",
    title: "Poly Bagging",
    desc: (
      <>
        Suffocation-warning poly bags applied in all Amazon-approved sizes per our{" "}
        <Link to="/blog/amazon-poly-bag-requirements-uk" style={inlineLinkOnLight}>poly bag requirements UK</Link> checklist.
      </>
    ),
  },
  { icon: "🫧", title: "Bubble Wrapping", desc: "Fragile products protected using professional-grade bubble wrap and cushioning." },
  {
    icon: "📦",
    title: "Bundle Creation",
    desc: (
      <>
        Multi-unit product bundles assembled to your exact specifications as part of wider{" "}
        <Link to="/amazon-fba-prep-services" style={inlineLinkOnLight}>Amazon FBA prep services</Link>.
      </>
    ),
  },
  {
    icon: "🔧",
    title: "Kitting Services",
    desc: (
      <>
        Complex kits assembled accurately with speed, consistency, and quality control at our{" "}
        <Link to="/amazon-fba-prep-centre-services" style={inlineLinkOnLight}>FBA prep centre</Link>.
      </>
    ),
  },
];

const STATS: Stat[] = [
  { value: 250, suffix: "+", label: "Happy Clients" },
  { value: 99.9, suffix: "%", label: "Accuracy Rate" },
  { value: 24, suffix: "hr", label: "Avg Turnaround" },
];

// ─── Counter Hook ─────────────────────────────────────────────────────────────
function useCounter(target: number, duration: number, trigger: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(parseFloat(start.toFixed(1)));
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return count;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ stat, trigger }: { stat: Stat; trigger: boolean }) {
  const count = useCounter(stat.value, 2000, trigger);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={trigger ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      style={{
        background: PAGE_BG,
        border: `1px solid rgba(${ACCENT_RGB}, 0.22)`,
        borderRadius: 20,
        padding: "40px 32px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
      }} />
      <div style={{
        fontSize: "clamp(3rem, 5vw, 4.5rem)",
        fontFamily: FONT_STACK,
        fontWeight: 800,
        color: ACCENT,
        lineHeight: 1,
        letterSpacing: "-0.04em",
      }}>
        {count}{stat.suffix}
      </div>
      <div style={{ color: TEXT_SOFT, marginTop: 10, fontSize: "1rem", fontFamily: FONT_STACK, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {stat.label}
      </div>
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PrivateLabelFBAPrep() {
  const statsRef = useRef<HTMLDivElement>(null);
  const statsInView = useInViewOnce(statsRef, { once: true, margin: "-100px" });
  const processRef = useRef<HTMLDivElement>(null);
  const processInView = useInViewOnce(processRef, { once: true, margin: "-80px" });
  const featuresRef = useRef<HTMLDivElement>(null);
  const featuresInView = useInViewOnce(featuresRef, { once: true, margin: "-80px" });

  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroReady(true), 100); return () => clearTimeout(t); }, []);

  return (
    <div style={{ background: PAGE_BG, minHeight: "100vh", color: TEXT_DARK, overflowX: "hidden" }}>
      {/* ── Google Fonts ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        html { scroll-behavior: smooth; }

        body { background: ${PAGE_BG}; font-family: ${FONT_STACK}; }

        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: ${ACCENT}; border-radius: 3px; }

        @keyframes underlineGrow {
          from { width: 0; }
          to { width: 100%; }
        }
        @keyframes floatY {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 24px rgba(${ACCENT_RGB}, 0.32), 0 0 48px rgba(${ACCENT_RGB}, 0.14); }
          50% { box-shadow: 0 0 40px rgba(${ACCENT_RGB}, 0.44), 0 0 80px rgba(${ACCENT_RGB}, 0.18); }
        }
        @keyframes scanLine {
          0% { top: 0%; }
          100% { top: 100%; }
        }
        @keyframes particleDrift {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.6; }
          100% { transform: translateY(-120vh) translateX(40px) rotate(360deg); opacity: 0; }
        }
        @keyframes bgShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes connectorFlow {
          0% { stroke-dashoffset: 600; }
          100% { stroke-dashoffset: 0; }
        }

        .step-card:hover .step-icon-wrap {
          transform: scale(1.15) rotate(-5deg);
        }
        .feature-card:hover {
          border-color: rgba(${ACCENT_RGB}, 0.45) !important;
          transform: translateY(-6px) !important;
          box-shadow: 0 18px 36px rgba(15,23,42,0.08);
        }
        .feature-card:hover .feat-icon {
          transform: scale(1.2);
        }
        .cta-btn:hover {
          transform: scale(1.06) !important;
        }

        @media (max-width: 768px) {
          .process-row { flex-direction: column !important; align-items: center !important; }
          .process-connector { width: 2px !important; height: 48px !important; margin: 0 !important; background: linear-gradient(to bottom, ${ACCENT}, transparent) !important; }
          .features-grid { grid-template-columns: 1fr 1fr !important; }
          .stats-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .features-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════════════ */}
      <section className="hero-section fba-page-hero">
        <PictureImage
          className="fba-page-hero__bg"
          src="amazonfba/privatelab.webp"
          alt="Private label FBA prep and quality inspection services UK"
          width={1600}
          height={900}
          decoding="async"
          isHero
        />
        <div className="fba-page-hero__overlay" aria-hidden />

        {/* Scan line effect */}
        <div style={{
          position: "absolute", left: 0, right: 0, height: "1px",
          background: `linear-gradient(90deg, transparent, rgba(${ACCENT_RGB}, 0.42), transparent)`,
          zIndex: 2, animation: "scanLine 6s linear infinite",
        }} />

        {/* Floating particles */}
        {[...Array(18)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            borderRadius: "50%",
            background: i % 3 === 2 ? "rgba(255,255,255,0.3)" : ACCENT,
            left: `${Math.random() * 100}%`,
            bottom: `-${Math.random() * 20}px`,
            zIndex: 2,
            animation: `particleDrift ${6 + Math.random() * 10}s linear ${Math.random() * 8}s infinite`,
          }} />
        ))}

        {/* Grid texture */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          backgroundImage: `
            linear-gradient(rgba(${ACCENT_RGB}, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(${ACCENT_RGB}, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }} />


        {/* Main hero content */}
        <div className="fba-page-hero__content">
          <div style={{ textAlign: "left", maxWidth: "min(900px, 100%)" }}>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={heroReady ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ marginBottom: 24 }}
          >
            <span style={{
              display: "inline-block",
              background: `rgba(${ACCENT_RGB}, 0.16)`,
              border: `1px solid rgba(${ACCENT_RGB}, 0.42)`,
              color: ACCENT,
              padding: "6px 18px",
              borderRadius: 50,
              fontSize: "0.75rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontFamily: FONT_STACK,
              fontWeight: 700,
            }}>
              Private Label Solutions
              </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={heroReady ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: FONT_STACK,
              fontWeight: 800,
              lineHeight: 0.95,
              color: "#fff",
              letterSpacing: "-0.04em",
              marginBottom: 16,
            }}
          >
            Private Label{' '}
            <span style={{ color: '#f97316', position: 'relative' }}>
              FBA Prep UK
              {heroReady && (
                <span style={{
                  position: "absolute",
                  bottom: -6, left: 0, height: 4,
                  background: ACCENT,
                  borderRadius: 2,
                  animation: "underlineGrow 1s ease 1.4s both forwards",
                  width: 0,
                }} />
              )}
            </span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={heroReady ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.9 }}
            style={{
              fontFamily: FONT_STACK,
              color: "rgba(255,255,255,0.82)",
              maxWidth: 640,
              margin: "0 0 40px",
              lineHeight: 1.65,
              fontWeight: 400,
            }}
          >
            Our private label prep services manage every stage from inventory receipt to Amazon-ready dispatch, helping brands focus on product development, marketing, and growth while we handle labelling, packaging, compliance, and preparation requirements. New to inbound rules? Read{" "}
            <Link to="/blog/what-is-fba-prep" style={inlineLinkOnDark}>what is FBA prep</Link>. For full service scope see our{" "}
            <Link to="/amazon-fba-prep-services" style={inlineLinkOnDark}>Amazon FBA prep service UK</Link> hub,{" "}
            <Link to="/amazon-fba-prep-pricing" style={inlineLinkOnDark}>FBA prep pricing</Link>, or our{" "}
            <Link to="/amazon-fba-prep-centre-services" style={inlineLinkOnDark}>prep centre facility</Link> tour.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroReady ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 1.1 }}
            style={{ display: "flex", gap: 16, justifyContent: "flex-start", flexWrap: "wrap" }}
          >
            <button
              className="cta-btn"
              style={{
                background: '#f97316',
                color: "#1a1a1a",
                border: "none",
                padding: "16px 36px",
                borderRadius: 50,
                fontSize: "1rem",
                fontFamily: FONT_STACK,
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.05em",
                animation: "glowPulse 3s ease infinite",
                transition: "transform 0.2s ease",
              }}
              
              onClick={() => document.getElementById("cta")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Your Free Quote →
            </button>
            <button
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                padding: "16px 36px",
                borderRadius: 50,
                fontSize: "1rem",
                fontFamily: FONT_STACK,
                fontWeight: 600,
                cursor: "pointer",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = ACCENT)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)")}
              onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
            >
              See How It Works ↓
            </button>
          </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={heroReady ? { opacity: 1 } : {}}
          transition={{ delay: 1.8, duration: 0.8 }}
          style={{
            position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
            zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          }}
        >
          <span style={{ fontFamily: FONT_STACK, fontSize: "0.65rem", color: "rgba(255,255,255,0.5)", letterSpacing: "0.2em", fontWeight: 700 }}>SCROLL</span>
          <div style={{
            width: 1, height: 48,
            background: `linear-gradient(to bottom, rgba(${ACCENT_RGB}, 0.62), transparent)`,
            animation: "floatY 2s ease infinite",
          }} />
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          PROCESS TIMELINE
      ══════════════════════════════════════════════════════════ */}
      <section id="process" style={{ padding: "56px 24px", background: PAGE_BG, position: "relative", borderTop: `1px solid ${PAGE_BORDER}` }}>
        {/* Section decorative top border */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, rgba(${ACCENT_RGB}, 0.24), rgba(${ACCENT_RGB}, 0.42), transparent)` }} />

        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: "center", marginBottom: 40 }}
          >
            <span style={{ fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.7rem", color: ACCENT, letterSpacing: "0.3em", textTransform: "uppercase", display: "block", marginBottom: 12 }}>
              Our Process
            </span>
            <h2 style={{ fontFamily: FONT_STACK, fontWeight: 800, color: TEXT_DARK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>
              FROM SUPPLIER TO{" "}
              <span style={{ color: ACCENT }}>AMAZON SHELF</span>
            </h2>
            <p style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, fontSize: "1rem" }}>
              Five Steps to Amazon-Ready Inventory
            </p>
          </motion.div>

          {/* Timeline */}
          <div ref={processRef} className="process-row" style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", flexWrap: "nowrap", gap: 0 }}>
            {STEPS.map((step, i) => (
              <div key={step.id} style={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
                {/* Step Card */}
                <motion.div
                  className="step-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={processInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    textAlign: "center", width: 180, padding: "0 8px",
                    cursor: "default",
                  }}
                >
                  {/* Step number */}
                  <div style={{ fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.65rem", color: `rgba(${ACCENT_RGB}, 0.78)`, letterSpacing: "0.15em", marginBottom: 12 }}>
                    STEP {String(step.id).padStart(2, "0")}
                  </div>

                  {/* Icon circle */}
                  <motion.div
                    className="step-icon-wrap"
                    whileHover={{ scale: 1.15, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{
                      width: 72, height: 72,
                      borderRadius: "50%",
                      background: `rgba(${ACCENT_RGB}, 0.1)`,
                      border: `2px solid rgba(${ACCENT_RGB}, 0.38)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "2rem",
                      marginBottom: 16,
                      boxShadow: `0 0 20px rgba(${ACCENT_RGB}, 0.12)`,
                    }}
                  >
                    {step.icon}
                  </motion.div>

                  <div style={{ fontFamily: FONT_STACK, fontWeight: 700, color: TEXT_DARK, fontSize: "0.9rem", marginBottom: 8, lineHeight: 1.2 }}>
                    {step.title}
                  </div>
                  <div style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, fontSize: "0.78rem", lineHeight: 1.5 }}>
                    {step.desc}
                  </div>
                </motion.div>

                {/* Connector */}
                {i < STEPS.length - 1 && (
                  <motion.div
                    className="process-connector"
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={processInView ? { scaleX: 1, opacity: 1 } : {}}
                    transition={{ duration: 0.5, delay: i * 0.15 + 0.4 }}
                    style={{
                      width: 48, height: 2,
                      background: `linear-gradient(90deg, rgba(${ACCENT_RGB}, 0.92), rgba(${ACCENT_RGB}, 0.4))`,
                      flexShrink: 0,
                      transformOrigin: "left center",
                      margin: "0 4px",
                      marginBottom: 60,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FEATURES GRID
      ══════════════════════════════════════════════════════════ */}
      <section style={{ padding: "56px 24px", background: PAGE_BG, position: "relative" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: "center", marginBottom: 40 }}
          >
            <span style={{ fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.7rem", color: ACCENT, letterSpacing: "0.3em", textTransform: "uppercase", display: "block", marginBottom: 12 }}>
              What We Offer
            </span>
            <h2 style={{ fontFamily: FONT_STACK, fontWeight: 800, color: TEXT_DARK, letterSpacing: "-0.04em" }}>
              EVERY SERVICE YOUR{" "}
              <span style={{ color: ACCENT }}>BRAND NEEDS</span>
            </h2>
          </motion.div>

          {/* Grid */}
          <div ref={featuresRef} className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {FEATURES.map((feat, i) => (
              <motion.div
                key={feat.title}
                className="feature-card"
                initial={{ opacity: 0, y: 40 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  background: PAGE_ALT,
                  border: `1px solid ${PAGE_BORDER}`,
                  borderRadius: 16,
                  transition: "all 0.3s ease",
                  cursor: "default",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Corner accent */}
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  width: 60, height: 60,
                  background: `radial-gradient(circle at top right, rgba(${ACCENT_RGB}, 0.08), transparent)`,
                }} />

                <div className="feat-icon" style={{ fontSize: "2.2rem", marginBottom: 18, display: "inline-block", transition: "transform 0.3s ease" }}>
                  {feat.icon}
                </div>
                <div style={{ fontFamily: FONT_STACK, fontWeight: 700, color: TEXT_DARK, fontSize: "1.05rem", marginBottom: 10 }}>
                  {feat.title}
                </div>
                <div style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, fontSize: "0.875rem", lineHeight: 1.6 }}>
                  {feat.desc}
                </div>
                <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 6, color: ACCENT, fontSize: "0.78rem", fontFamily: FONT_STACK, fontWeight: 700, letterSpacing: "0.08em" }}>
                  <span style={{ width: 20, height: 1, background: ACCENT, display: "inline-block" }} />
                  INCLUDED
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          WHY CHOOSE US — STATS
      ══════════════════════════════════════════════════════════ */}
      <section style={{ padding: "56px 24px", background: PAGE_BG, position: "relative", overflow: "hidden" }}>
        {/* Background glow */}
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600, height: 600,
          background: `radial-gradient(circle, rgba(${ACCENT_RGB}, 0.06) 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: "center", marginBottom: 40 }}
          >
            <span style={{ fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.7rem", color: ACCENT, letterSpacing: "0.3em", textTransform: "uppercase", display: "block", marginBottom: 12 }}>
              Why Choose Us
            </span>
            <h2 style={{ fontFamily: FONT_STACK, fontWeight: 800, color: TEXT_DARK, letterSpacing: "-0.04em" }}>
              NUMBERS THAT{" "}
              <span style={{ color: ACCENT }}>SPEAK FOR THEMSELVES</span>
            </h2>
            <p style={{ fontFamily: FONT_STACK, color: TEXT_SOFT,  fontSize: "1rem", maxWidth: 560, margin: "16px auto 0" }}>
              Trusted by growing private label brands for accuracy, speed, and reliability. Compare{" "}
              <Link to="/amazon-fba-prep-pricing" style={inlineLinkOnLight}>FBA prep pricing UK</Link> and plan for{" "}
              <Link to="/blog/amazon-ending-fba-prep-services-uk-2026" style={inlineLinkOnLight}>July 2026 Amazon prep changes</Link> before your next inbound.
            </p>
          </motion.div>

          <div ref={statsRef} className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {STATS.map((stat) => (
              <StatCard key={stat.label} stat={stat} trigger={statsInView} />
            ))}
          </div>

          {/* Additional trust points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ marginTop: 36, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 32 }}
          >
            {["Amazon TOS Compliant", "Real-time Tracking", "Dedicated Account Manager", "Insured Warehouse"].map(point => (
              <div key={point} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: FONT_STACK, fontSize: "0.9rem", color: TEXT_MID }}>
                <span style={{ color: ACCENT, fontSize: "1rem" }}>✓</span>
                {point}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          CTA SECTION
      ══════════════════════════════════════════════════════════ */}
      <section id="cta" style={{ padding: "140px 24px", background: PAGE_BG, position: "relative", overflow: "hidden", borderTop: `1px solid ${PAGE_BORDER}` }}>
        {/* Diagonal lines decoration */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(${ACCENT_RGB}, 0.015) 40px, rgba(${ACCENT_RGB}, 0.015) 41px)`,
          zIndex: 0,
        }} />

        {/* Glow blobs */}
        <div style={{
          position: "absolute", top: "20%", left: "10%",
          width: 400, height: 400,
          background: `radial-gradient(circle, rgba(${ACCENT_RGB}, 0.08) 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "20%", right: "10%",
          width: 300, height: 300,
          background: "radial-gradient(circle, rgba(244,165,36,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Decorative bracket */}
            <div style={{ fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.7rem", color: `rgba(${ACCENT_RGB}, 0.78)`, letterSpacing: "0.3em", marginBottom: 28 }}>
              ─── ✦ READY TO SCALE ✦ ───
            </div>

            <h2 style={{
              fontFamily: FONT_STACK,
              fontWeight: 800,
              color: TEXT_DARK,
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
              marginBottom: 28,
            }}>
              START YOUR{" "}
              <span style={{ color: ACCENT, display: "block" }}>PRIVATE LABEL</span>
              JOURNEY
            </h2>

            <p style={{
              fontFamily: FONT_STACK,
              fontSize: "1.1rem",
              color: TEXT_SOFT,
              lineHeight: 1.7,
              maxWidth: 520,
              margin: "0 auto 32px",
            }}>
              Join growing private label brands that trust our private label prep services.{" "}
              <Link to="/pricing-fulfilment-services" style={inlineLinkOnLight}>Request a free quote</Link> today and receive a tailored response within two business hours — or{" "}
              <Link to="/contact-fulfillment-services" style={inlineLinkOnLight}>contact our prep team</Link> directly.
            </p>

            {/* Main CTA button */}
            <Link to="/contact-fulfillment-services" style={{ textDecoration: "none" }}>
            <motion.button
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: ACCENT,
                color: "#ffffff",
                border: "none",
                padding: "20px 56px",
                borderRadius: 50,
                fontSize: "1.1rem",
                fontFamily: FONT_STACK,
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                animation: "glowPulse 3s ease infinite",
                display: "inline-block",
                marginBottom: 20,
              }}
            >
              Get Your Free Quote →
            </motion.button>
            </Link>

            <div style={{ display: "block" }}>
              <span style={{ fontFamily: FONT_STACK, fontSize: "0.8rem", color: TEXT_SOFT }}>
                No commitment required · Free onboarding consultation
              </span>
            </div>

          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
